import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CardActionType, type CardActionItem } from './index.type'
import { Toast, Dialog } from 'react-vant'
import { useUserInfo } from '@/hooks'
import { copyToClipboard } from '@/utils'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import CustomButton from '@/components/CustomButton'
import { createAvatar } from '@dicebear/core'
import { identicon } from '@dicebear/collection'
import LogoTitle from '@/components/logoTitle'
import Ripple from '@/components/Ripple'
export default React.memo(function My() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { userInfo, actions: userActions } = useUserInfo()
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)

  // 进入页面刷新用户信息
  useEffect(() => {
    userActions.refreshUserInfo()
  }, [])

  // 生成用户头像
  const avatarSvg = useMemo(() => {
    const seed = userInfo?.email || userInfo?.xid || 'default-user'
    const avatar = createAvatar(identicon, {
      seed: String(seed),
      size: 80,
      backgroundColor: ['#f0f0f0', '#e0e0e0', '#d0d0d0'],
    })
    return avatar.toDataUri()
  }, [userInfo?.email, userInfo?.xid])

  const cardActionItems = useMemo<CardActionItem[]>(
    () => ([
      {
        icon: "/icon/my-card.svg",
        title: t("持卡人信息"),
        actionType: CardActionType.CardUser
      },
      // {
      //   icon: "/icon/my-address.svg",
      //   title: t("邮寄地址"),
      //   actionType: CardActionType.ShippingAddress
      // },
      {
        icon: "/icon/my-safety.svg",
        title: t("安全中心"),
        actionType: CardActionType.Safety
      },
      {
        icon: "/icon/my-lang.svg",
        title: t("语言设置"),
        actionType: CardActionType.Lang
      },
      {
        icon: "/icon/my-question.svg",
        title: t("帮助中心"),
        actionType: CardActionType.Help
      },
      {
        icon: "/icon/my-about.svg",
        title: t("关于我们"),
        actionType: CardActionType.About
      },
    ]),
    [t]
  )
  const handleCardAction = useCallback((_actionType: CardActionType) => {
    if (_actionType === CardActionType.CardUser) {
      navigate('/my/cardholder')
    } else if (_actionType === CardActionType.Safety) {
      navigate('/safety')
    } else if (_actionType === CardActionType.ShippingAddress) {
      navigate('/card-v2/add-shipping-address')
    } else if (_actionType === CardActionType.Lang) {
      navigate('/my/set-lang')
    } else if (_actionType === CardActionType.Help) {
      navigate('/my/help')
    } else if (_actionType === CardActionType.About) {
      navigate('/my/about')
    }
  }, [navigate])

  // 处理退出登录
  const handleLogout = useCallback(() => {
    // 清除token和app_state，但保留多语言缓存
    localStorage.removeItem('token')
    localStorage.removeItem('app_state')

    Toast.info({
      message: t('退出登录'),
      duration: 1000,
      onClose: () => {
        // 跳转到登录页面
        navigate('/login', { replace: true })
      }
    })
  }, [navigate, t])

  // 显示退出登录对话框
  const handleShowLogoutDialog = useCallback(() => {
    setShowLogoutDialog(true)
  }, [])

  // 关闭退出登录对话框
  const handleCloseLogoutDialog = useCallback(() => {
    setShowLogoutDialog(false)
  }, [])

  return (
    <PageLayout tabbarPage={true}>
      <Header
        centerChildren={<LogoTitle />}
      />
      <div className="px-6 py-6">
        <div
          className="flex justify-between items-start cursor-pointer"
          onClick={() => navigate('/my/edit-profile')}
        >
          <div className='w-20 h-20 bg-[#D9D9D9] rounded-full mr-3 flex-shrink-0 overflow-hidden flex items-center justify-center'>
            <img className="w-full h-full object-cover" src={userInfo?.avatar_url || avatarSvg} alt="用户头像" />
          </div>
          <div className='flex-1 min-w-0'>
            <div className='text-lg font-500 text-[#1A1A1A] mt-2 truncate text-ellipsis overflow-hidden whitespace-nowrap'>{userInfo?.nickname || userInfo?.email || '--'}</div>
            <div className='text-[#626262] text-3.25 mt-1 flex items-center justify-between'>
              <div className='flex items-center'>
                <span>UID:{userInfo?.xid || '-'}</span>
                {userInfo?.xid && (
                  <img
                    src="/images/card-v2/copy.svg"
                    alt="copy"
                    className="w-4 h-4 ml-2 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation()
                      copyToClipboard(String(userInfo.xid || ''))
                    }}
                  />
                )}
              </div>
              <div>
                <CustomButton
                  size="small"
                  color="primary"
                  onClick={() => navigate('/my/edit-profile')}
                >
                  <img src="/images/my/icons/edit-white.svg" alt="edit" className="w-4 h-4 mr-2" />
                  {t('编辑')}
                </CustomButton>
              </div>
            </div>
            {/* {userInfo?.kyc_status && (
              <div className='mt-3 flex-inline rounded-2.5 bg-[#F7F7F7] text-[#556AFF] text-2.5 px-4 py-1.5 flex justify-center items-center'>{t('预审核通过')}</div>
            )} */}
          </div>
        </div>
        <div className="w-full flex flex-col items-center space-y-5 mt-6">
          {cardActionItems.map(item => (
            <Ripple key={item.title} className="rounded-3xl w-full">
              <div
                key={item.title}
                className="w-full flex h-17 justify-between items-center border-[0.5px] border-solid border-[#E5E5E5] rounded-3xl px-5 cursor-pointer"
                onClick={() => handleCardAction(item.actionType)}
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center mr-1">
                    <img src={item.icon} alt={item.title} />
                  </div>
                  <div className="flex flex-col">
                    <div className="text-base font-500 text-black">{item.title}</div>
                  </div>
                </div>
                <div><img src="/icon/right-info.svg" /></div>
              </div>
            </Ripple>
          ))}
        </div>
        <div className="my-6">
          <CustomButton
            color="danger"
            onClick={handleShowLogoutDialog}
          >
            {t('退出登录')}
          </CustomButton>
        </div>

        {/* 退出登录确认对话框 */}
        <Dialog
          visible={showLogoutDialog}
          onClose={handleCloseLogoutDialog}
          closeable={true}
          showCancelButton={false}
          showConfirmButton={false}
          width="90%"
        >
          <div className="text-center px-6 py-8">
            <div className="text-xl font-500 text-black mb-4 break-all px-5">{t('退出登录')}</div>
            <div className="text-base text-gray-600 mb-6">
              {t('确定要退出登录吗')}
            </div>

            {/* 操作按钮 */}
            <div className="flex justify-center items-center space-x-3">
              <CustomButton
                onClick={handleCloseLogoutDialog}
                color="default"
              >
                {t('取消')}
              </CustomButton>
              <CustomButton
                onClick={() => {
                  handleCloseLogoutDialog()
                  handleLogout()
                }}
                color="danger"
              >
                {t('确定')}
              </CustomButton>
            </div>
          </div>
        </Dialog>
      </div>
    </PageLayout>
  )
})